import {
    forwardRef,
    useEffect,

    useState,
    useRef
} from "react";
import Button from "react-bootstrap/Button";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import Box from "@mui/material/Box";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Alert from "@mui/material/Alert";
import AppTableView from "../AppTableView";
import { styled, useTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import LinearProgress from "@mui/material/LinearProgress";

import {
    ASSET_ATTACHMENTS_BY_ITEM_URL,
    ASSET_ATTACHMENT_URL,
} from "./../../Constants/Constants";

import AxiosInstance from "./../../services/AxiosInstance";
import { useAlert } from "./../alerts/AlertContext";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/gif",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "text/plain",
    "application/octet-stream",
];

const DragDropArea = styled(Box)(({ isdragover }) => ({
    border: "2px dashed #1976d2",
    borderRadius: 8,
    padding: 24,
    marginBottom: 16,
    textAlign: "center",
    color: "#1976d2",
    cursor: "pointer",
    background: isdragover ? "#e3f2fd" : "#f5faff",
    transition: "background 0.2s",
}));

function formatFileSize(bytes) {
    if (typeof bytes !== "number" || isNaN(bytes) || bytes <= 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getMimeType(extension) {
    const map = {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        gif: "image/gif",
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        xls: "application/vnd.ms-excel",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        txt: "text/plain",
    };
    return map[extension?.toLowerCase()] || "application/octet-stream";
}

function isImage(extension) {
    return ["jpg", "jpeg", "png", "gif"].includes(extension?.toLowerCase());
}


function AssetAttachment({ editAssetId, selectedAssetName }, ref) {

    const [files, setFiles] = useState([]); // [{file, error, progress, status}]
    const [status, setStatus] = useState("initial");
    const [data, setData] = useState([]);
    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef();
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [preview, setPreview] = useState({ open: false, url: "", name: "" });
    const theme = useTheme();
  
    useEffect(() => {
        AxiosInstance.get(`${ASSET_ATTACHMENTS_BY_ITEM_URL}/${editAssetId}`).then(
            (response) => {
                setData(response.data.data);
            }
        );
    }, [editAssetId]);


    // Handle file selection via input
    const handleFileInputChange = (e) => {
        const newFiles = Array.from(e.target.files).map((file) => {
            const error = validateFile(file);
            return { file, error, progress: 0, status: "pending" };
        });
        setFiles((prev) => {
            // Avoid duplicates by name+size
            const all = [...prev];
            newFiles.forEach((nf) => {
                if (
                    !all.some(
                        (pf) =>
                            pf.file.name === nf.file.name && pf.file.size === nf.file.size
                    )
                ) {
                    all.push(nf);
                }
            });
            return all;
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // Handle drag-and-drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const newFiles = Array.from(e.dataTransfer.files).map((file) => {
            const error = validateFile(file);
            return { file, error, progress: 0, status: "pending" };
        });
        setFiles((prev) => {
            const all = [...prev];
            newFiles.forEach((nf) => {
                if (
                    !all.some(
                        (pf) =>
                            pf.file.name === nf.file.name && pf.file.size === nf.file.size
                    )
                ) {
                    all.push(nf);
                }
            });
            return all;
        });
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    // Remove a file from the list
    const handleRemoveFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Validate file
    const validateFile = (file) => {
        if (!ALLOWED_TYPES.includes(file.type)) {
            return "Invalid file type";
        }
        if (file.size > MAX_FILE_SIZE) {
            return "File too large (max 10MB)";
        }
        return null;
    };

    // Upload all valid files
    const handleUpload = async () => {
        if (!files.length) return;
        setStatus("uploading");
        let uploaded = [];
        let newFiles = [...files];
        for (let i = 0; i < newFiles.length; i++) {
            if (newFiles[i].error) continue;
            newFiles[i].status = "uploading";
            setFiles([...newFiles]);
            let formData = new FormData();
            formData.append("file", newFiles[i].file);
            formData.append("assetId", editAssetId);
            try {
                const response = await AxiosInstance.post(ASSET_ATTACHMENT_URL, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (progressEvent) => {
                        const percent = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        newFiles[i].progress = percent;
                        setFiles([...newFiles]);
                    },
                });
                uploaded.push(response.data.data);
                newFiles[i].status = "done";
                setFiles([...newFiles]);
            } catch (err) {
                newFiles[i].status = "fail";
                setFiles([...newFiles]);
                setNotification({
                    open: true,
                    message: `Failed to upload ${newFiles[i].file.name}`,
                    severity: "error",
                });
                setStatus("fail");
                return;
            }
        }
        setData((prev) => [...prev, ...uploaded]);
        setStatus("success");
        setFiles([]);
        setNotification({
            open: true,
            message: "File(s) uploaded successfully!",
            severity: "success",
        });
    };

    // Download attachment with correct MIME and extension
    const handleDownload = (attachId, attachFileName, fileExt) => {
        AxiosInstance.get(`${ASSET_ATTACHMENT_URL}/${attachId}`, {
            responseType: "blob",
        }).then((response) => {
            const mimeType = getMimeType(fileExt);
            const fileURL = window.URL.createObjectURL(
                new Blob([response.data], { type: mimeType })
            );
            let alink = document.createElement("a");
            alink.href = fileURL;
            alink.setAttribute("download", attachFileName);
            document.body.appendChild(alink);
            alink.click();
            alink.parentNode.removeChild(alink);
        });
    };

    // Delete attachment
    const handleDeleteAttachment = (id) => {
        AxiosInstance.delete(`${ASSET_ATTACHMENT_URL}/${id}`)
            .then(() => {
                setData((prev) => prev.filter((row) => row.id !== id));
                setNotification({
                    open: true,
                    message: "Attachment deleted",
                    severity: "success",
                });
            })
            .catch(() => {
                setNotification({
                    open: true,
                    message: "Failed to delete attachment",
                    severity: "error",
                });
            })
            .finally(() => setDeleteDialog({ open: false, id: null }));
    };

    // Image preview logic
    const handlePreview = (attachId, fileExt, name) => {
        AxiosInstance.get(`${ASSET_ATTACHMENT_URL}/${attachId}`, {
            responseType: "blob",
        }).then((response) => {
            const mimeType = getMimeType(fileExt);
            const fileURL = window.URL.createObjectURL(
                new Blob([response.data], { type: mimeType })
            );
            setPreview({ open: true, url: fileURL, name });
        });
    };

    const columns = [
        //{ field: "title", headerName: "Title", flex: 2 },
        { field: "name", headerName: "File Name", flex: 2 },
        { field: "extension", headerName: "Type", flex: 1 },
        {
            field: "size",
            headerName: "Size",
            flex: 1,
            renderCell: ({ row }) => formatFileSize(row.size),
        },
        {
            field: "download",
            headerName: "Download",
            flex: 1,
            renderCell: ({ row }) => (
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleDownload(row.id, row.name, row.extension)}
                >
                    <DownloadIcon fontSize="small" />
                </IconButton>
            ),
        },
        {
            field: "preview",
            headerName: "Preview",
            flex: 1,
            renderCell: ({ row }) => (
                <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handlePreview(row.id, row.extension, row.name)}
                >
                    <VisibilityIcon fontSize="small" />
                </IconButton>
            ),
        },
        {
            field: "delete",
            headerName: "Delete",
            flex: 1,
            renderCell: ({ row }) => (
                <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialog({ open: true, id: row.id })}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            ),
        },
    ];

    return (
        <ThemeProvider>
            <Box sx={{ width: "100%", p: 2, bgcolor: theme.palette.background.default }}>
                <Typography variant="h6" gutterBottom>
                    Attachments
                </Typography>
                <Box sx={{ width: "100%", mb: 2, p: 2 }}>
                    {/* Drag-and-drop area and file input */}
                    <DragDropArea
                        isdragover={isDragOver ? 1 : 0}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onClick={() =>
                            !status.startsWith("upload") &&
                            fileInputRef.current &&
                            fileInputRef.current.click()
                        }
                    >
                        Drag and drop files here, or click to select files
                        <input
                            type="file"
                            multiple
                            style={{ display: "none" }}
                            ref={fileInputRef}
                            onChange={handleFileInputChange}
                            disabled={status === "uploading"}
                        />
                    </DragDropArea>
                    {/* List of selected files with remove buttons, validation, and progress */}
                    {files.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            {files.map((fobj, idx) => (
                                <Box
                                    key={fobj.file.name + fobj.file.size + idx}
                                    sx={{ display: "flex", alignItems: "center", mb: 1 }}
                                >
                                    <Typography variant="body2" sx={{ flex: 2 }}>
                                        {fobj.file.name} ({formatFileSize(fobj.file.size)})
                                    </Typography>
                                    {fobj.error && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{ flex: 2, ml: 2 }}
                                        >
                                            {fobj.error}
                                        </Typography>
                                    )}
                                    {fobj.status === "uploading" && (
                                        <Box sx={{ flex: 2, ml: 2, minWidth: 120 }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={fobj.progress}
                                            />
                                            <Typography variant="caption">
                                                {fobj.progress}%
                                            </Typography>
                                        </Box>
                                    )}
                                    {fobj.status === "done" && (
                                        <Typography
                                            variant="caption"
                                            color="success.main"
                                            sx={{ flex: 2, ml: 2 }}
                                        >
                                            Uploaded
                                        </Typography>
                                    )}
                                    {fobj.status === "fail" && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            sx={{ flex: 2, ml: 2 }}
                                        >
                                            Failed
                                        </Typography>
                                    )}
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveFile(idx)}
                                        disabled={status === "uploading"}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        disabled={
                            files.length === 0 ||
                            status === "uploading" ||
                            files.every((f) => f.error)
                        }
                        onClick={handleUpload}
                        startIcon={
                            status === "uploading" ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : null
                        }
                    >
                        {status === "uploading" ? "Uploading..." : "Upload"}
                    </Button>
                </Box>
                <Box sx={{ width: "100%", mb: 2, p: 2 }}>
                    <AppTableView rows={data} cols={columns} initialPageSize={5} />
                </Box>
                {/* Snackbar for notifications */}
                <Snackbar
                    open={notification.open}
                    autoHideDuration={4000}
                    onClose={() => setNotification({ ...notification, open: false })}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert
                        severity={notification.severity}
                        onClose={() => setNotification({ ...notification, open: false })}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
                {/* Delete confirmation dialog */}
                <Dialog
                    open={deleteDialog.open}
                    onClose={() => setDeleteDialog({ open: false, id: null })}
                >
                    <DialogTitle>Delete Attachment?</DialogTitle>
                    <DialogContent>
                        Are you sure you want to delete this attachment?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
                            Cancel
                        </Button>
                        <Button
                            color="error"
                            onClick={() => handleDeleteAttachment(deleteDialog.id)}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* Image preview modal */}
                <Dialog
                    open={preview.open}
                    onClose={() => setPreview({ open: false, url: "", name: "" })}
                    maxWidth="md"
                >
                    <DialogTitle>{preview.name}</DialogTitle>
                    <DialogContent>
                        <img
                            src={preview.url}
                            alt={preview.name}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "70vh",
                                display: "block",
                                margin: "auto",
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setPreview({ open: false, url: "", name: "" })}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider>
    );
}

export default forwardRef(AssetAttachment);
